import { useRef, useCallback, useEffect } from 'react';
import isEqual from 'lodash/isEqual';

import api from 'utils/api';
import useMergeState from '../mergeState';
import useDeepCompareMemoize from '../deepCompareMemoize';

const useQuery = (url, propsVariables = {}, options = {}) => {
  const {
    lazy = false,
    cachePolicy = 'cache-first',
    retryCount = 0,
    retryDelay = 1000,
  } = options;

  const wasCalled = useRef(false);
  const propsVariablesMemoized = useDeepCompareMemoize(propsVariables);

  const isSleeping = lazy && !wasCalled.current;
  const isCacheAvailable =
    cache[url] && isEqual(cache[url].apiVariables, propsVariables);
  const canUseCache =
    isCacheAvailable && cachePolicy !== 'no-cache' && !wasCalled.current;

  const [state, mergeState] = useMergeState({
    data: canUseCache ? cache[url].data : null,
    error: null,
    isLoading: !lazy && !canUseCache,
    variables: {},
  });

  const makeRequest = useCallback(
    (newVariables, attempt = 0) => {
      const variables = { ...state.variables, ...(newVariables || {}) };
      const apiVariables = { ...propsVariablesMemoized, ...variables };

      const skipLoading = canUseCache && cachePolicy === 'cache-first';

      if (!skipLoading) {
        mergeState({ isLoading: true, variables });
      } else if (newVariables) {
        mergeState({ variables });
      }
      api.get(url, apiVariables).then(
        (data) => {
          cache[url] = { data, apiVariables };
          mergeState({ data, error: null, isLoading: false });
        },
        (error) => {
          console.log('error in query', error);
          //! Retry logic
          if (attempt < retryCount) {
            setTimeout(() => {
              makeRequest(newVariables, attempt + 1);
            }, retryDelay * Math.pow(2, attempt)); // Exponential backoff
          } else {
            mergeState({ error, data: null, isLoading: false });
          }
        }
      );

      wasCalled.current = true;
    },
    [propsVariablesMemoized, url, retryCount, retryDelay]
  );

  //! This is the useEffect that loops
  useEffect(() => {
    if (isSleeping) return;
    if (canUseCache && cachePolicy === 'cache-only') return;
    makeRequest();
  }, [makeRequest, url]);

  // useCallback returns a memoized version of the callback function that only changes
  // if one of the specified dependencies has changed.
  // Therefore, `setLocalData` is the memoized version of the function inside `useCallback(...)`;
  const setLocalData = useCallback(
    (getUpdatedData) => {
      return mergeState(
        ({ data }) => {
          const updatedData = getUpdatedData(data);
          cache[url] = { ...(cache[url] || {}), data: updatedData };
          return { data: updatedData };
        },
        [mergeState, url]
      );
    },
    [mergeState, url]
  );

  return [
    {
      ...state,
      variables: { ...propsVariablesMemoized, ...state.variables },
      setLocalData,
    },
    makeRequest,
  ];
};

const cache = {};

export default useQuery;
